import React from 'react';
import { CheckCircle2, ClipboardCheck, Warehouse, Truck, FlaskConical } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { FadeUp, StaggerContainer, StaggerItem, ScaleIn } from '../components/ScrollReveal';

const QUALITY_IMG = 'https://customer-assets.emergentagent.com/job_bb23729c-0dea-4683-8280-e0aa97a36711/artifacts/9f2ikqv7_IMG_2754.jpeg';
const serif = { fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic', fontWeight: 400 };

const Quality = () => {
  const { lang } = useLanguage();
  const t = translations[lang].quality;

  // Dictionnaire local pour forcer tes textes exacts tout en gardant le bilinguisme
  const customTexts = {
    fr: {
      s1Title: "Sourçage direct",
      s1Desc: "Nous travaillons directement avec les producteurs ou les organismes locaux, sans intermédiaire. Cela garantit la traçabilité et des conditions équitables pour tous.",
      s2Title: "Partenariats durables",
      s2Desc: "Nos relations avec les producteurs et organismes partenaires s’inscrivent dans la durée. Relation fondée sur l’honnêteté et la confiance.",
      s3Title: "", // On retire le titre pour la 2ème rubrique Partenariat
      c1Name: "Agriculture biologique",
      c1Desc: "Agriculture respectueuse de la nature, des sols, des animaux et de la santé, sans produits chimiques de synthèse."
    },
    en: {
      s1Title: "Direct Sourcing",
      s1Desc: "We work directly with producers or local organizations, without intermediaries. This ensures traceability and fair conditions for all.",
      s2Title: "Sustainable Partnerships",
      s2Desc: "Our relationships with partner producers and organizations are built for the long term. A relationship based on honesty and trust.",
      s3Title: "", // Title removed
      c1Name: "Organic Farming",
      c1Desc: "Farming that respects nature, soil, animals, and health, without synthetic chemicals."
    }
  };

  const current = customTexts[lang] || customTexts.fr;

  const standards = [
    { key: 's1', icon: ClipboardCheck },
    { key: 's2', icon: FlaskConical },
    { key: 's3', icon: CheckCircle2 },
    { key: 's4', icon: CheckCircle2 },
  ];

  // Suppression des étapes 1 et 2, décalage des autres
  const processSteps = [
    { key: 'step3', num: '01' },
    { key: 'step4', num: '02' },
    { key: 'step5', num: '03' },
  ];

  // Ajout des liens vers tes vrais logos stockés dans le dossier public
  const certifications = [
    { c: 'c1', color: '#5B8A5B', bg: '#F0F7F0', img: '/Agriculture_biologique.png' },
    { c: 'c2', color: '#C9A84C', bg: '#FDF9EC', img: '/Ecocert.png' },
    { c: 'c3', color: '#4B7BAB', bg: '#EDF3FA', img: '/Fair_Trade.png' },
    { c: 'c4', color: '#8B4513', bg: '#FAF0EA', img: '/USDA_Organic.png' },
  ];

  const getStandardTitle = (key) => {
    if (key === 's1') return current.s1Title;
    if (key === 's2') return current.s2Title;
    if (key === 's3') return current.s3Title;
    return t[`${key}Title`];
  };

  const getStandardDesc = (key) => {
    if (key === 's1') return current.s1Desc;
    if (key === 's2') return current.s2Desc;
    return t[`${key}Desc`];
  };

  const getCertName = (c) => {
    if (c === 'c1') return current.c1Name;
    return t[`${c}Name`];
  };

  const getCertDesc = (c) => {
    if (c === 'c1') return current.c1Desc;
    return t[`${c}Desc`];
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end pb-16 overflow-hidden" data-testid="quality-hero">
        <div className="absolute inset-0 z-0">
          <img src={QUALITY_IMG} alt="Quality vanilla" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#2C2416]/55" />
        </div>
        <div className="relative z-10 container-max pt-28 text-center w-full">
          <FadeUp>
            <p className="section-overline">{t.overline}</p>
            <h1 className="text-5xl md:text-7xl text-white leading-tight mb-4" style={serif}>
              {t.heroTitle}<br /><em className="not-italic" style={{ color: '#E8D5A0' }}>{t.heroTitleAccent}</em>
            </h1>
            <div className="w-10 h-px bg-[#C9A84C] mx-auto mt-4" />
          </FadeUp>
        </div>
      </section>

      {/* STANDARDS */}
      <section className="section-padding bg-[#FAF7F2]" data-testid="quality-standards">
        <div className="container-max">
          <FadeUp>
            <p className="section-overline text-center">{t.standardsOverline}</p>
            <h2 className="text-4xl md:text-5xl text-center text-[#2C2416] mb-12" style={serif}>{t.standardsTitle}</h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {standards.map(({ key, icon: Icon }, i) => {
              const title = getStandardTitle(key);
              return (
                <FadeUp key={key} delay={i * 0.08}>
                  <div className="bg-white border border-[#E5DDD2] rounded-xl p-7 flex items-start gap-5 hover:border-[#C9A84C]/40 hover:shadow-sm transition-all h-full">
                    <div className="icon-badge shrink-0">
                      <Icon size={18} className="text-[#8B4513]" />
                    </div>
                    <div>
                      {title && <h3 className="text-base font-semibold text-[#2C2416] mb-2">{title}</h3>}
                      <p className="text-[#7A6E63] text-sm leading-relaxed">{getStandardDesc(key)}</p>
                    </div>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS (dark) */}
      <section className="section-padding bg-[#2B2016]" data-testid="quality-process">
        <div className="container-max">
          <FadeUp>
            <p className="text-[10px] uppercase tracking-[0.35em] text-[#C9A84C] mb-3 text-center">{t.processOverline}</p>
            <h2 className="text-4xl text-center text-white mb-14" style={serif}>{t.processTitle}</h2>
          </FadeUp>

          <div className="relative max-w-2xl mx-auto">
            <div className="absolute left-5 top-0 bottom-0 w-px bg-white/10" />
            <div className="space-y-8">
              {processSteps.map(({ key, num }, i) => (
                <FadeUp key={key} delay={i * 0.08}>
                  <div className="flex gap-8 items-start">
                    <div className="w-10 h-10 rounded-full border border-[#C9A84C]/40 flex items-center justify-center shrink-0 z-10 bg-[#2B2016]"
                      data-testid={`process-step-${i}`}>
                      <span className="text-[#C9A84C] text-[10px] font-bold">{num}</span>
                    </div>
                    <div className="flex-1 pt-1">
                      <h3 className="text-base text-white mb-1" style={{ fontFamily: '"Cormorant Garamond", serif' }}>{t[`${key}Title`]}</h3>
                      <p className="text-white/50 text-xs leading-relaxed">{t[`${key}Desc`]}</p>
                    </div>
                  </div>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="section-padding bg-[#F0EBE0]" data-testid="quality-certifications">
        <div className="container-max">
          <FadeUp>
            <p className="section-overline text-center">{t.certifOverline}</p>
            <h2 className="text-4xl text-center text-[#2C2416] mb-4" style={serif}>{t.certifTitle}</h2>
            <div className="gold-divider mx-auto" />
            <p className="text-[#7A6E63] text-center max-w-xl mx-auto mt-4 mb-12 text-sm leading-relaxed">{t.certifDesc}</p>
          </FadeUp>

          <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-5" staggerDelay={0.1}>
            {certifications.map(({ c, color, bg, img }) => (
              <StaggerItem key={c}>
                <div className="rounded-xl p-7 text-center border border-[#E5DDD2] hover:shadow-md transition-all h-full flex flex-col justify-between"
                  style={{ backgroundColor: bg }} data-testid="cert-badge">

                  {/* Utilisation des vrais logos ici */}
                  <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-5 border border-[#E5DDD2] bg-white p-3 shadow-sm"
                    style={{ borderColor: `${color}40` }}>
                    <img src={img} alt={getCertName(c)} className="w-full h-full object-contain" />
                  </div>

                  <div>
                    <h3 className="font-serif text-[#2C2416] text-sm mb-2">{getCertName(c)}</h3>
                    <p className="text-[#7A6E63] text-[11px] leading-relaxed">{getCertDesc(c)}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>
    </div>
  );
};

export default Quality;